/** TanStack Query hooks for the admin API. */
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export const qk = {
  me: ["admin-me"] as const,
  config: ["config"] as const,
  overview: ["overview"] as const,
  users: (search: string, page: number) => ["users", search, page] as const,
  user: (id: string) => ["user", id] as const,
  ledger: (id: string, page: number) => ["ledger", id, page] as const,
  withdrawals: (status: string, userId?: string) => ["withdrawals", status, userId ?? ""] as const,
  deposits: (status: string, userId?: string) => ["deposits", status, userId ?? ""] as const,
  limits: ["limits"] as const,
  hotWallets: ["hot-wallets"] as const,
  reconciliation: ["reconciliation"] as const,
  audit: (filter: object) => ["audit", filter] as const,
  signer: ["signer"] as const,
  staff: ["staff"] as const,
  roles: ["roles"] as const,
  swaps: (status: string, userId: string | undefined, page: number) =>
    ["swaps", status, userId ?? "", page] as const,
};

export const useConfig = () =>
  useQuery({ queryKey: qk.config, queryFn: () => api.config(), staleTime: Infinity });
export const useAdminMe = () => useQuery({ queryKey: qk.me, queryFn: () => api.adminMe() });
export const useOverview = (enabled = true) =>
  useQuery({ queryKey: qk.overview, queryFn: () => api.overview(), refetchInterval: 15_000, enabled });
export const useUsers = (search: string, page: number) =>
  useQuery({ queryKey: qk.users(search, page), queryFn: () => api.users(search, page) });
export const useUser = (id: string) => useQuery({ queryKey: qk.user(id), queryFn: () => api.user(id) });
export const useLedger = (id: string, page: number) =>
  useQuery({ queryKey: qk.ledger(id, page), queryFn: () => api.userLedger(id, page) });
export const useWithdrawals = (status: string, userId?: string) =>
  useQuery({
    queryKey: qk.withdrawals(status, userId),
    queryFn: () => api.adminWithdrawals(status, userId),
    refetchInterval: 15_000,
  });
export const useDeposits = (status: string, userId?: string) =>
  useQuery({
    queryKey: qk.deposits(status, userId),
    queryFn: () => api.adminDeposits(status, userId),
    refetchInterval: 20_000,
  });
export const useLimits = () => useQuery({ queryKey: qk.limits, queryFn: () => api.limits() });
export const useHotWallets = () =>
  useQuery({ queryKey: qk.hotWallets, queryFn: () => api.hotWallets(), refetchInterval: 30_000 });
export const useReconciliation = () =>
  useQuery({ queryKey: qk.reconciliation, queryFn: () => api.reconciliation() });
export const useAudit = (filter: { actorType?: string; action?: string; target?: string; page: number }) =>
  useQuery({ queryKey: qk.audit(filter), queryFn: () => api.audit(filter) });
export const useSwaps = (status: string, userId: string | undefined, page: number) =>
  useQuery({
    queryKey: qk.swaps(status, userId, page),
    queryFn: () => api.adminSwaps({ status, ...(userId ? { userId } : {}), page }),
    refetchInterval: 20_000,
  });
export const useSigner = () =>
  useQuery({ queryKey: qk.signer, queryFn: () => api.signer(), refetchInterval: 15_000 });
